import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "preset",
    "scope",
    "warning",
    "expiry",
    "expiryPreset",
    "expiryCustom",
    "projectList",
    "project",
    "bindingAll",
    "resourceRow",
    "resourceCount",
    "emptyScopes",
    "addMenu",
    "addPanel",
    "addQuery",
    "addList",
    "addOption",
    "addBtn",
    "addCheck",
    "addRealmHeader",
    "realmHeader",
    "availableCount",
    "scopeStrings",
    "scopeCount",
    "accountSelect",
  ];

  static values = { writeExpiryDays: { type: Number, default: 90 } };

  connect() {
    this.expiryIsTheirs =
      this.hasExpiryTarget && this.expiryTarget.value !== "";
    this.bindingChanged();
    if (
      this.hasExpiryPresetTarget &&
      this.expiryPresetTarget.value === "custom"
    )
      this.expiryCustomTarget?.classList.remove("d-none");
    this.#syncEditor();
    this.#syncWriteVisuals();

    this.dismissAdd = (event) => {
      if (!this.hasAddMenuTarget) return;
      if (this.addMenuTarget.contains(event.target)) return;
      this.closeAdd();
    };
    document.addEventListener("click", this.dismissAdd);
  }

  disconnect() {
    document.removeEventListener("click", this.dismissAdd);
  }

  expiryChanged() {
    this.expiryIsTheirs = true;
  }

  accountChanged() {
    if (!this.hasAccountSelectTarget) return;
    const url = this.accountSelectTarget.dataset.scopesUrl;
    const accountId = this.accountSelectTarget.value;
    const frame = document.getElementById("token_projects");
    if (frame) frame.src = `${url}?account_id=${accountId}`;
  }

  applyPreset(event) {
    const preset = event.target.dataset;
    const bound = this.hasBindingAllTarget && !this.bindingAllTarget.checked;
    const source =
      bound && preset.scopesBound !== undefined
        ? preset.scopesBound
        : preset.scopes;
    const wanted = new Set((source || "").split(/\s+/).filter(Boolean));

    this.scopeTargets.forEach((box) => {
      box.checked = wanted.has(box.value) && !box.disabled;
    });

    this.#syncWarnings();
    this.#suggestExpiry();
    this.#resetRowsToScopes();
    this.#syncEditor();
    this.#syncWriteVisuals();
  }

  scopeChanged() {
    this.#syncWarnings();
    this.#syncEditor();
    this.#syncWriteVisuals();
  }

  bindingChanged() {
    const boundToProjects = !this.bindingAllTarget.checked;

    this.projectListTarget.classList.toggle("d-none", !boundToProjects);

    this.#accountRealmScopes().forEach((box) => {
      box.disabled = boundToProjects;
      if (boundToProjects) box.checked = false;
    });

    this.#syncWarnings();
    this.#syncEditor();
    this.#syncWriteVisuals();
  }

  // --- grant editor -------------------------------------------------------

  addResource(event) {
    const resource = event.currentTarget.dataset.resource;
    const row = this.#row(resource);
    if (!row) return;

    row.classList.remove("d-none");
    const read = this.#scopesFor(resource).find(
      (box) => box.value.endsWith(":read") && !box.disabled,
    );
    if (read) read.checked = true;

    this.#syncWarnings();
    this.#syncEditor();
    this.#syncWriteVisuals();

    this.filterAdd();
    if (this.hasAddQueryTarget) this.addQueryTarget.focus();
  }

  removeResource(event) {
    const resource = event.currentTarget.dataset.resource;
    const row = this.#row(resource);
    if (!row) return;

    row.classList.add("d-none");
    this.#scopesFor(resource).forEach((box) => {
      box.checked = false;
    });

    this.#syncWarnings();
    this.#syncEditor();
    this.#syncWriteVisuals();
  }

  toggleAdd() {
    if (!this.hasAddPanelTarget) return;
    const open = this.addPanelTarget.classList.toggle("show");
    if (open && this.hasAddQueryTarget) {
      this.addQueryTarget.value = "";
      this.filterAdd();
      this.addQueryTarget.focus();
    }
  }

  filterAdd() {
    if (!this.hasAddQueryTarget) return;
    const query = this.addQueryTarget.value.trim().toLowerCase();

    const visibleByRealm = { project: 0, account: 0 };

    this.addOptionTargets.forEach((option) => {
      const resource = option.dataset.resource;
      const hidden =
        !resource.includes(query) ||
        this.#isShown(resource) ||
        this.#unavailable(resource);
      option.classList.toggle("d-none", hidden);
      if (!hidden) visibleByRealm[option.dataset.displayGroup]++;
    });

    // Show/hide realm headers based on whether any options are visible
    this.addRealmHeaderTargets.forEach((header) => {
      const group = header.dataset.displayGroup;
      header.classList.toggle("d-none", !visibleByRealm[group]);
    });

    this.addCheckTargets.forEach((check) => {
      const shown = this.#isShown(check.dataset.resource);
      check.classList.toggle("c-scope-check--active", shown);
    });
  }

  addKeydown(event) {
    if (event.key === "Escape") return this.closeAdd();
    if (event.key !== "Enter") return;

    event.preventDefault();
    const first = this.addOptionTargets.find(
      (option) => !option.classList.contains("d-none"),
    );
    if (first) first.click();
  }

  expiryPresetChanged() {
    this.expiryIsTheirs = true;
    this.#applyExpiryPreset();
  }

  #applyExpiryPreset() {
    if (!this.hasExpiryPresetTarget || !this.hasExpiryTarget) return;

    const chosen = this.expiryPresetTarget.selectedOptions[0];
    const value = chosen ? chosen.value : "none";
    const custom = value === "custom";

    if (this.hasExpiryCustomTarget)
      this.expiryCustomTarget.classList.toggle("d-none", !custom);

    if (custom) {
      // Leave whatever is already there
    } else if (value === "none") {
      this.expiryTarget.value = "";
    } else {
      this.expiryTarget.value = chosen.dataset.date || "";
    }
  }

  #row(resource) {
    return this.resourceRowTargets.find(
      (row) => row.dataset.resource === resource,
    );
  }

  #isShown(resource) {
    const row = this.#row(resource);
    return row ? !row.classList.contains("d-none") : false;
  }

  #scopesFor(resource) {
    return this.scopeTargets.filter(
      (box) => box.value.split(":")[0] === resource,
    );
  }

  #unavailable(resource) {
    const boxes = this.#scopesFor(resource);
    return boxes.length > 0 && boxes.every((box) => box.disabled);
  }

  closeAdd() {
    if (this.hasAddPanelTarget) this.addPanelTarget.classList.remove("show");
  }

  #revealRowsWithScopes() {
    this.resourceRowTargets.forEach((row) => {
      const checked = this.#scopesFor(row.dataset.resource).some(
        (box) => box.checked,
      );
      if (checked) row.classList.remove("d-none");
    });
  }

  #resetRowsToScopes() {
    this.resourceRowTargets.forEach((row) => {
      const checked = this.#scopesFor(row.dataset.resource).some(
        (box) => box.checked,
      );
      row.classList.toggle("d-none", !checked);
    });
  }

  #syncEditor() {
    if (!this.hasResourceRowTarget) return;

    this.#revealRowsWithScopes();

    this.resourceRowTargets.forEach((row) => {
      if (this.#unavailable(row.dataset.resource)) row.classList.add("d-none");
    });

    const available = this.resourceRowTargets.filter(
      (row) => !this.#unavailable(row.dataset.resource),
    );
    const shown = available.filter((row) => !row.classList.contains("d-none"));
    const scopes = this.#checkedScopes().sort();

    // Show/hide realm section headers based on visible rows
    this.#syncRealmHeaders(shown);

    if (this.hasEmptyScopesTarget) {
      this.emptyScopesTarget.classList.toggle("d-none", shown.length > 0);
    }
    if (this.hasResourceCountTarget) {
      this.resourceCountTarget.textContent = shown.length
        ? `${shown.length} ${shown.length === 1 ? "resource" : "resources"}`
        : "";
    }
    if (this.hasAvailableCountTarget) {
      const left = available.length - shown.length;
      this.availableCountTarget.textContent = left
        ? `${left} more available`
        : "all resources added";
    }
    if (this.hasScopeCountTarget) {
      this.scopeCountTarget.textContent = `${scopes.length} ${
        scopes.length === 1 ? "scope" : "scopes"
      }`;
    }
    if (this.hasScopeStringsTarget) this.#renderScopeStrings(scopes);
    if (this.hasAddBtnTarget) {
      this.addBtnTarget.disabled = shown.length >= available.length;
    }
  }

  #syncRealmHeaders(shownRows) {
    if (!this.hasRealmHeaderTarget) return;

    const visibleByGroup = { project: 0, account: 0 };
    shownRows.forEach((row) => {
      const group = row.dataset.displayGroup;
      if (group) visibleByGroup[group]++;
    });

    const hasAccount = visibleByGroup.account > 0;

    this.realmHeaderTargets.forEach((header) => {
      const group = header.dataset.displayGroup;
      if (group === "project") {
        header.classList.toggle(
          "d-none",
          !hasAccount || !visibleByGroup.project,
        );
      } else if (group === "account") {
        header.classList.toggle("d-none", !hasAccount);
      }
    });
  }

  // Sync the visual write checkbox indicator with the hidden input state
  #syncWriteVisuals() {
    this.element.querySelectorAll(".scope-write-input").forEach((input) => {
      const label = input.previousElementSibling;
      if (!label) return;
      const indicator = label.querySelector(".c-scope-check-fill");
      if (!indicator) return;
      indicator.classList.toggle("c-scope-check-fill--on", input.checked);
    });
  }

  #renderScopeStrings(scopes) {
    this.scopeStringsTarget.replaceChildren();

    if (scopes.length === 0) {
      const empty = document.createElement("span");
      empty.className = "text-body-secondary";
      empty.textContent = "No scopes selected.";
      this.scopeStringsTarget.append(empty);
      return;
    }

    scopes.forEach((scope) => {
      const code = document.createElement("code");
      code.textContent = scope;
      this.scopeStringsTarget.append(code);
    });
  }

  #accountRealmScopes() {
    return this.scopeTargets.filter((box) => box.dataset.realm === "account");
  }

  #checkedScopes() {
    return this.scopeTargets
      .filter((box) => box.checked)
      .map((box) => box.value);
  }

  #syncWarnings() {
    const checked = new Set(this.#checkedScopes());
    let hasVisible = false;

    this.warningTargets.forEach((warning) => {
      const show = checked.has(warning.dataset.scope);
      warning.classList.toggle("d-none", !show);
      if (show) hasVisible = true;
    });

    if (hasVisible) {
      const details = this.warningTargets[0]?.closest("details");
      if (details) details.open = true;
    }
  }

  #suggestExpiry() {
    if (!this.hasExpiryTarget || this.expiryIsTheirs || this.expiryTarget.value)
      return;
    if (!this.#checkedScopes().some((scope) => !scope.endsWith(":read")))
      return;

    if (this.hasExpiryPresetTarget) {
      const option = Array.from(this.expiryPresetTarget.options).find(
        (o) => o.value === String(this.writeExpiryDaysValue),
      );
      if (option) {
        this.expiryPresetTarget.value = option.value;
        this.#applyExpiryPreset();
        return;
      }
    }

    const when = new Date();
    when.setDate(when.getDate() + this.writeExpiryDaysValue);
    this.expiryTarget.value = when.toISOString().slice(0, 10);
  }
}
